import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import ContentCopyIcon from '@mui/icons-material/ContentCopy'; // Icono para copiar
import VisibilityIcon from '@mui/icons-material/Visibility'; // Icono para mostrar/ocultar
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'; // Icono para mostrar/ocultar
import LinkIcon from '@mui/icons-material/Link'; // Icono para URL
import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography, Select, MenuItem,
    FormControl, InputLabel, Stack, CircularProgress, Snackbar, Alert, IconButton,
    InputAdornment, Tooltip, Autocomplete // Importar Autocomplete
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'; // Icono para añadir
import BuildIcon from '@mui/icons-material/Build'; // <-- ÍCONO AÑADIDO

// Interfaz para el objeto de Dominio de Tracker (para el selector)
interface TrackerDomain {
    id: number;
    domain_url: string;
    status: 'active' | 'inactive';
}

const PostbackToolPage: React.FC = () => {
    // --- ESTADOS ---
    const [trackerDomains, setTrackerDomains] = useState<TrackerDomain[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    // Estados para los parámetros del postback
    const [selectedDomainId, setSelectedDomainId] = useState<number | ''>('');
    const [clickId, setClickId] = useState<string>('REPLACE_CLICKID');
    const [payout, setPayout] = useState<string>('0.00');
    const [status, setStatus] = useState<string>('approved');
    const [txid, setTxid] = useState<string>('');
    const [sub1, setSub1] = useState<string>('');
    const [sub2, setSub2] = useState<string>('');
    const [showOptionalParams, setShowOptionalParams] = useState<boolean>(false);

    const [generatedUrl, setGeneratedUrl] = useState<string>('');
    
    // Estados para mensajes de "Copiado"
    const [copyUrlSuccess, setCopyUrlSuccess] = useState<boolean>(false);
    const [copySdkSuccess, setCopySdkSuccess] = useState<boolean>(false);
    const [copyClickoutSuccess, setCopyClickoutSuccess] = useState<boolean>(false);
    // NUEVO ESTADO: Para el éxito de copiado del script de push
    const [copyPushScriptSuccess, setCopyPushScriptSuccess] = useState<boolean>(false);


    // Función para obtener los dominios disponibles
    const fetchTrackerDomains = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            // Usando axios para consistencia con otros componentes
            const response = await axios.get(`${API_BASE_URL}/tracker-domains`);
            const data: TrackerDomain[] = response.data;
            
            setTrackerDomains(data);
            const firstActiveDomain = data.find((d: TrackerDomain) => d.status === 'active');
            if (firstActiveDomain) {
                setSelectedDomainId(firstActiveDomain.id);
            } else if (data.length > 0) {
                // Si no hay activos, selecciona el primero disponible
                setSelectedDomainId(data[0].id); 
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error desconocido al cargar dominios.');
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al cargar dominios.' });
            console.error("Error fetching tracker domains:", err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTrackerDomains();
    }, [fetchTrackerDomains]);

    // Función para construir la URL del postback dinámicamente
    const buildPostbackUrl = useCallback(() => {
        const selectedDomain = trackerDomains.find(d => d.id === selectedDomainId);
        if (!selectedDomain) {
            setGeneratedUrl('');
            return;
        }

        let params = new URLSearchParams();
        params.append('click_id', clickId);
        params.append('payout', parseFloat(payout).toFixed(2)); // Asegura formato de dos decimales
        if (status !== 'approved') {
            params.append('status', status);
        }
        if (txid) params.append('txid', txid);
        if (sub1) params.append('sub1', sub1);
        if (sub2) params.append('sub2', sub2);

        setGeneratedUrl(`${selectedDomain.domain_url}/postback?${params.toString()}`);
    }, [selectedDomainId, clickId, payout, status, txid, sub1, sub2, trackerDomains]);

    useEffect(() => {
        buildPostbackUrl();
    }, [buildPostbackUrl]);

    // Función para copiar texto al portapapeles
    const copyTextToClipboard = useCallback(async (text: string, setSuccessState: React.Dispatch<React.SetStateAction<boolean>>) => {
        try {
            // Usar document.execCommand para compatibilidad en entornos de iframe
            const tempInput = document.createElement('textarea');
            tempInput.value = text;
            document.body.appendChild(tempInput);
            tempInput.select();
            document.execCommand('copy');
            document.body.removeChild(tempInput);

            setSuccessState(true);
            setTimeout(() => setSuccessState(false), 2000); // Reset after 2 seconds
            setNotification({ type: 'success', message: '¡Copiado al portapapeles!' });
        } catch (err) {
            setNotification({ type: 'error', message: 'Error al copiar. Por favor, copia manualmente.' });
            console.error('Error al copiar:', err);
        }
    }, []);

    // Lógica para generar el código del SDK
    const getLpSdkCode = useCallback(() => {
        const selectedDomain = trackerDomains.find(d => d.id === selectedDomainId);
        const domainForSdk = selectedDomain ? selectedDomain.domain_url.replace(/https?:\/\//, '') : 'tu-dominio-tracker.com';
        return `<script src="https://${domainForSdk}/lp-sdk.js"></script>`;
    }, [selectedDomainId, trackerDomains]);

    // Código para el link de salida
    const clickoutLinkCode = '<a href="#" id="clickout-link">Tu Link</a>';

    // NUEVO: Código para el script de notificaciones push
    const pushNotificationScriptCode = '<script type="module" src="/permission.js"></script>';

    // --- RENDERIZADO ---
    if (isLoading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Cargando dominios disponibles...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">Error: {error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Snackbar para notificaciones */}
            <Snackbar
                open={!!notification}
                autoHideDuration={6000}
                onClose={() => setNotification(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>
                    {notification?.message}
                </Alert>
            </Snackbar>

            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <BuildIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Generador de URLs y Herramientas
            </Typography>

            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
                <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                    Configuración de Postback
                </Typography>
                <Box component="form" onSubmit={(e) => e.preventDefault()} noValidate>
                    <Stack spacing={3}>
                        {/* Autocomplete para Dominio del Tracker */}
                        <Autocomplete
                            fullWidth
                            options={trackerDomains}
                            getOptionLabel={(option) => option.domain_url.replace(/https?:\/\//, '') + (option.status !== 'active' ? ' (inactivo)' : '')}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={trackerDomains.find(d => d.id === selectedDomainId) || null}
                            onChange={(event, newValue) => {
                                setSelectedDomainId(newValue ? newValue.id : '');
                            }}
                            renderInput={(params) => <TextField {...params} label="Dominio del Tracker" variant="outlined" required />}
                            filterOptions={(options, { inputValue }) => {
                                const lowercasedInput = inputValue.toLowerCase();
                                return options.filter(option =>
                                    option.domain_url.toLowerCase().includes(lowercasedInput) ||
                                    String(option.id).includes(lowercasedInput)
                                );
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Click ID"
                            id="click_id_input"
                            value={clickId}
                            onChange={(e) => setClickId(e.target.value)}
                            placeholder="Click ID (ej. {clickid})"
                            required
                            variant="outlined"
                        />

                        <TextField
                            fullWidth
                            label="Payout"
                            type="number"
                            inputProps={{ step: "0.01" }}
                            id="payout_input"
                            value={payout}
                            onChange={(e) => setPayout(e.target.value)}
                            placeholder="Payout (ej. 10.50)"
                            variant="outlined"
                        />

                        <FormControl fullWidth variant="outlined">
                            <InputLabel id="status-select-label">Estado</InputLabel>
                            <Select
                                labelId="status-select-label"
                                id="status_select"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                label="Estado"
                            >
                                <MenuItem value="approved">approved (por defecto)</MenuItem>
                                <MenuItem value="pending">pending</MenuItem>
                                <MenuItem value="rejected">rejected</MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            variant="outlined"
                            onClick={() => setShowOptionalParams(!showOptionalParams)}
                            startIcon={showOptionalParams ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            sx={{ mt: 2 }}
                        >
                            {showOptionalParams ? 'Ocultar Parámetros Opcionales' : 'Mostrar Parámetros Opcionales'}
                        </Button>

                        {showOptionalParams && (
                            <Stack spacing={3}>
                                <TextField
                                    fullWidth
                                    label="Transaction ID (txid) (opc.)"
                                    id="txid_input"
                                    value={txid}
                                    onChange={(e) => setTxid(e.target.value)}
                                    placeholder="Transaction ID (ej. {txid})"
                                    variant="outlined"
                                />
                                <TextField
                                    fullWidth
                                    label="SubID 1 (opc.)"
                                    id="sub1_input"
                                    value={sub1}
                                    onChange={(e) => setSub1(e.target.value)}
                                    placeholder="SubID 1"
                                    variant="outlined"
                                />
                                <TextField
                                    fullWidth
                                    label="SubID 2 (opc.)"
                                    id="sub2_input"
                                    value={sub2}
                                    onChange={(e) => setSub2(e.target.value)}
                                    placeholder="SubID 2"
                                    variant="outlined"
                                />
                            </Stack>
                        )}
                    </Stack>
                </Box>
            </Paper>

            {/* URL de Postback Generada */}
            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
                <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                    URL de Postback Generada:
                </Typography>
                <TextField
                    fullWidth
                    value={generatedUrl}
                    variant="outlined"
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title={copyUrlSuccess ? 'Copiado!' : 'Copiar URL'}>
                                    <IconButton
                                        onClick={() => copyTextToClipboard(generatedUrl, setCopyUrlSuccess)}
                                        edge="end"
                                        color={copyUrlSuccess ? 'success' : 'primary'}
                                    >
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ),
                        startAdornment: (
                            <InputAdornment position="start">
                                <LinkIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ '& .MuiInputBase-input': { cursor: 'pointer' } }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Haz clic en la URL o en el icono para copiarla directamente.
                </Typography>
            </Paper>

            {/* Código para Landing Pages */}
            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
                <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                    Código para Landing Pages
                </Typography>
                
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>Paso 1: Pega el SDK en tu página.</Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={getLpSdkCode()}
                    variant="outlined"
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title={copySdkSuccess ? 'Copiado!' : 'Copiar SDK'}>
                                    <IconButton
                                        onClick={() => copyTextToClipboard(getLpSdkCode(), setCopySdkSuccess)}
                                        edge="end"
                                        color={copySdkSuccess ? 'success' : 'primary'}
                                    >
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    }}
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', cursor: 'copy' } }}
                />

                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>Paso 2: Usa este código para tus links de salida.</Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={1}
                    value={clickoutLinkCode}
                    variant="outlined"
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title={copyClickoutSuccess ? 'Copiado!' : 'Copiar Link'}>
                                    <IconButton
                                        onClick={() => copyTextToClipboard(clickoutLinkCode, setCopyClickoutSuccess)}
                                        edge="end"
                                        color={copyClickoutSuccess ? 'success' : 'primary'}
                                    >
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    }}
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', cursor: 'copy' } }}
                />

                {/* NUEVO: Paso 3: Script para Notificaciones Push */}
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>Paso 3: Agrega este script para Notificaciones Push.</Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={1}
                    value={pushNotificationScriptCode}
                    variant="outlined"
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title={copyPushScriptSuccess ? 'Copiado!' : 'Copiar Script'}>
                                    <IconButton
                                        onClick={() => copyTextToClipboard(pushNotificationScriptCode, setCopyPushScriptSuccess)}
                                        edge="end"
                                        color={copyPushScriptSuccess ? 'success' : 'primary'}
                                    >
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    }}
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', cursor: 'copy' } }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Este script debe ir en tu página para activar las notificaciones push. Asegúrate de que `permission.js` y `firebase-messaging-sw.js` estén en la raíz de tu dominio.
                </Typography>
            </Paper>
        </Container>
    );
};

export default PostbackToolPage;